import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import { TailSpin } from "react-loader-spinner";
import "./countrywiseOTPCountV2.css";

const API_URL =
  "https://qzufib1cx8.execute-api.us-east-1.amazonaws.com/dev/country-wise-otp-count";

const CountryWiseOTPCountV2 = () => {
  const currentDate = new Date();
  const startOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );

  const [startDate, setStartDate] = useState(startOfMonth);
  const [endDate, setEndDate] = useState(currentDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");

  const formatDateToUTCString = (date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
      .toISOString()
      .split("T")[0];
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(API_URL, {
        start_date: formatDateToUTCString(startDate),
        end_date: formatDateToUTCString(endDate),
      });
      const fetchedData = response.data;
      if (fetchedData.mali_per_date) {
        fetchedData.mali_per_date.sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );
      }
      if (fetchedData.ghana_per_date) {
        fetchedData.ghana_per_date.sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );
      }
      setData(fetchedData);
    } catch (error) {
      console.error("Error fetching data", error);
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const dates = useMemo(
    () => (data?.mali_per_date ? data.mali_per_date.map((e) => e.date) : []),
    [data],
  );

  const maliCounts = useMemo(
    () => (data?.mali_per_date ? data.mali_per_date.map((e) => e.count) : []),
    [data],
  );

  const ghanaCounts = useMemo(
    () => (data?.ghana_per_date ? data.ghana_per_date.map((e) => e.count) : []),
    [data],
  );

  const guineaCounts = useMemo(
    () =>
      data?.guinea_per_date ? data.guinea_per_date.map((e) => e.count) : [],
    [data],
  );

  const totalMali = useMemo(
    () => (maliCounts?.length ? maliCounts.reduce((a, c) => a + c, 0) : 0),
    [maliCounts],
  );

  const totalGhana = useMemo(
    () => (ghanaCounts?.length ? ghanaCounts.reduce((a, c) => a + c, 0) : 0),
    [ghanaCounts],
  );

  const totalGuinea = useMemo(
    () => (guineaCounts?.length ? guineaCounts.reduce((a, c) => a + c, 0) : 0),
    [guineaCounts],
  );

  const tableData = useMemo(
    () =>
      dates.map((date, i) => ({
        date,
        maliCount: maliCounts[i] ?? 0,
        ghanaCount: ghanaCounts[i] ?? 0,
        guineaCount: guineaCounts[i] ?? 0,
      })),
    [dates, maliCounts, ghanaCounts, guineaCounts],
  );

  const chartData = {
    labels: dates,
    datasets: [
      {
        label: "Mali OTP Count",
        data: maliCounts,
        backgroundColor: "rgba(56, 189, 248, 0.7)",
        borderColor: "rgba(14, 165, 233, 1)",
        borderWidth: 1,
        borderRadius: 6,
        fill: chartType === "line",
      },
      {
        label: "Ghana OTP Count",
        data: ghanaCounts,
        backgroundColor: "rgba(16, 185, 129, 0.7)",
        borderColor: "rgba(5, 150, 105, 1)",
        borderWidth: 1,
        borderRadius: 6,
        fill: chartType === "line",
      },
      {
        label: "Guinea OTP Count",
        data: guineaCounts,
        backgroundColor: "rgba(245, 158, 11, 0.7)",
        borderColor: "rgba(217, 119, 6, 1)",
        borderWidth: 1,
        borderRadius: 6,
        fill: chartType === "line",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 12 } } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, font: { size: 11 } },
      },
      y: {
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 } },
      },
    },
  };

  return (
    <div className="country-otp-v2">
      <div className="country-otp-v2-header">
        <h1 className="country-otp-v2-title">Country Wise OTP Statistics</h1>
        <div className="country-otp-v2-controls">
          <div className="country-otp-v2-date-row">
            <DatePicker
              selected={startDate}
              onChange={(d) => setStartDate(d)}
              className="country-otp-v2-datepicker"
              dateFormat="yyyy-MM-dd"
              placeholderText="Start Date"
            />
            <DatePicker
              selected={endDate}
              onChange={(d) => setEndDate(d)}
              minDate={startDate}
              className="country-otp-v2-datepicker"
              dateFormat="yyyy-MM-dd"
              placeholderText="End Date"
            />
            <button className="country-otp-v2-apply" onClick={fetchData}>
              Apply
            </button>
          </div>
          <select
            className="country-otp-v2-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
          </select>
        </div>
      </div>

      <div className="country-otp-v2-cards">
        <div className="country-otp-v2-stat-card mali">
          <div className="country-otp-v2-stat-label">Total OTPs Mali</div>
          <div className="country-otp-v2-stat-value">
            {totalMali.toLocaleString()}
          </div>
        </div>
        <div className="country-otp-v2-stat-card ghana">
          <div className="country-otp-v2-stat-label">Total OTPs Ghana</div>
          <div className="country-otp-v2-stat-value">
            {totalGhana.toLocaleString()}
          </div>
        </div>
        <div className="country-otp-v2-stat-card guinea">
          <div className="country-otp-v2-stat-label">Total OTPs Guinea</div>
          <div className="country-otp-v2-stat-value">
            {totalGuinea.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="country-otp-v2-main">
        <div className="country-otp-v2-chart-card">
          {loading ? (
            <div className="country-otp-v2-loading">
              <TailSpin
                height={48}
                width={48}
                color="#38bdf8"
                ariaLabel="loading"
              />
            </div>
          ) : (
            <div className="country-otp-v2-chart-wrapper">
              {chartType === "bar" ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <Line
                  data={{
                    ...chartData,
                    datasets: chartData.datasets.map((ds, i) => ({
                      ...ds,
                      fill: true,
                      tension: 0.3,
                      backgroundColor:
                        i === 0
                          ? "rgba(56, 189, 248, 0.2)"
                          : i === 1
                          ? "rgba(16, 185, 129, 0.2)"
                          : "rgba(245, 158, 11, 0.2)",
                    })),
                  }}
                  options={chartOptions}
                />
              )}
            </div>
          )}
        </div>

        <div className="country-otp-v2-table-card">
          <div className="country-otp-v2-table-wrapper">
            <table className="country-otp-v2-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mali Count</th>
                  <th>Ghana Count</th>
                  <th>Guinea Count</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => (
                  <tr key={row.date}>
                    <td>{row.date}</td>
                    <td>{row.maliCount.toLocaleString()}</td>
                    <td>{row.ghanaCount.toLocaleString()}</td>
                    <td>{row.guineaCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryWiseOTPCountV2;
